'use strict'

import ElementPropertiesManager from 'assets/core/js/module/elementPropertiesManager'
import SearchCalendar, { RefreshDatesPlugin } from '@ui/Search/Calendar/component'

import type {
  Calendar as SearchCalendarType,
  PluginsConfig,
  RefreshDatesPlugin as RefreshDatesPluginType,
  RangeSelectionPlugin,
} from '@ui/Search/Calendar/component'

const calendarElementId = 'checkInDate'

/**
 * onChangeCallback will trigger an update of all accommodations
 */
const initCalendar = (onChangeCallback?: () => void): SearchCalendarType | void => {
  const inputEl = document.querySelector<HTMLElement>(`#${calendarElementId}`)

  if (!inputEl) {
    return
  }

  const setNightsField = (calendar: SearchCalendarType, value?: number): void => {
    if (calendar.element.hasAttribute('data-night-field')) {
      const nightFieldName = calendar.element.getAttribute('data-night-field')
      const nightFieldEl = document.querySelector<HTMLInputElement>(`[name="${nightFieldName}"]`)

      if (nightFieldEl && value && nightFieldEl.value !== value.toString()) {
        nightFieldEl.value = value.toString()
      }

      if (nightFieldEl && !value) {
        nightFieldEl.value = ''
      }
    }
  }

  const getAllNights = (calendar: SearchCalendarType): string | null => {
    if (calendar.element.hasAttribute('data-night-field')) {
      const nightFieldName = calendar.element.getAttribute('data-night-field')
      const nightFieldEl = document.querySelector<HTMLInputElement>(`[name="${nightFieldName}"]`)

      if (!nightFieldEl) {
        return null
      }

      if (!nightFieldEl.hasAttribute('data-values')) {
        return null
      }

      return nightFieldEl.getAttribute('data-values')
    }

    return null
  }

  const calendar = SearchCalendar(document.getElementById('search-form-calendar') as HTMLElement, {
    currentLocale: document.body.getAttribute('data-locale')?.split('_'),
    pluginsConfig: {
      refreshDates: (calendar) => {
        let firstDate = null

        const firstDateInputEl = document.getElementById('firstCheckInDateAvailable') as HTMLInputElement

        if (firstDateInputEl && firstDateInputEl.value !== '') {
          const firstCheckInDate = firstDateInputEl.value
          const splittedDate = firstCheckInDate.split('-')
          firstDate = `${splittedDate[0] as string}-${splittedDate[1] as string}-01`
        }

        return {
          ajaxUrl: calendar.input.getAttribute('data-url-get-available-dates') as string,
          // since the value of the nights field can change, we want a callback to be sure the value is up to date
          ajaxData: () => {
            const nights = getAllNights(calendar)

            return nights !== '' && nights !== null
              ? {
                  productId: calendar.input.getAttribute('data-product-id') as string,
                  nights,
                  numberOfMonths: calendar.calendar.config.showMonths,
                }
              : undefined
          },
          firstDate,
        }
      },
      rangeSelection: (calendar) => ({
        ajaxUrl: calendar.input.getAttribute('data-url-get-available-checkout-dates'),
        ajaxData: {
          productId: calendar.input.getAttribute('data-product-id') as string,
          nights: getAllNights(calendar) ?? '',
        },
        onValueChange: (nights) => {
          setNightsField(calendar, nights)

          onChangeCallback && onChangeCallback()
        },
      }),
    } as {
      rangeSelection: PluginsConfig<RangeSelectionPlugin['config']>
      refreshDates: PluginsConfig<RefreshDatesPluginType['config']>
    },
  })

  return calendar
}

export default function (onChangeCallback?: () => void): void {
  const calendar = initCalendar(onChangeCallback)

  if (!calendar) {
    return
  }

  const nightEl = document.getElementById('night') as HTMLInputElement

  nightEl &&
    nightEl.addEventListener('change', function () {
      const calendar = ElementPropertiesManager.getProperty<SearchCalendarType>(
        document.getElementById(calendarElementId) as HTMLInputElement,
        'calendar'
      )
      const refreshDatesPlugin = calendar?.plugins.refreshDates
      refreshDatesPlugin && (refreshDatesPlugin as RefreshDatesPlugin).updateAvailableDates()
    })
}
